import { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { BlurView } from 'expo-blur';
import { useRouter } from 'expo-router';

const COOKIE_CONSENT_KEY = 'cookie_consent_status'; // "accepted" | "rejected"

type CookieConsentStatus = 'accepted' | 'rejected' | null;

type CookieConsentProps = {
  policyUrl?: string;
  onStatusChange?: (status: Exclude<CookieConsentStatus, null>) => void;
};

// ✅ צבע מותג אחיד (מתאים ל-theme שעשינו)
const BRAND_PURPLE = '#B026FF';
const LIGHT_PURPLE_BG = 'rgba(176, 38, 255, 0.06)';

export function CookieConsent({
  policyUrl = '/legal/privacy',
  onStatusChange,
}: CookieConsentProps) {
  const router = useRouter();
  const { height: screenHeight } = useWindowDimensions();
  const cardMaxHeight = Math.min(320, Math.round(screenHeight * 0.34));
  const descriptionMaxHeight = Math.min(140, Math.round(screenHeight * 0.16));
  const [visible, setVisible] = useState(false);

  const loadConsent = useCallback(async () => {
    try {
      const stored = await AsyncStorage.getItem(COOKIE_CONSENT_KEY);
      if (!stored) {
        setVisible(true);
      } else {
        if (onStatusChange && (stored === 'accepted' || stored === 'rejected')) {
          onStatusChange(stored);
        }
      }
    } catch (error) {
      console.error('Error checking cookie consent:', error);
      setVisible(true);
    }
  }, [onStatusChange]);

  useEffect(() => {
    (async () => {
      await loadConsent();
    })();
  }, [loadConsent]);

  const setConsent = async (status: Exclude<CookieConsentStatus, null>) => {
    try {
      await AsyncStorage.setItem(COOKIE_CONSENT_KEY, status);
      setVisible(false);
      if (onStatusChange) onStatusChange(status);
    } catch (error) {
      console.error('Error saving cookie consent:', error);
      setVisible(false);
    }
  };

  const handleAccept = () => setConsent('accepted');
  const handleReject = () => setConsent('rejected');
  const handleOpenPolicy = () => router.push(policyUrl as any);

  if (!visible) return null;

  return (
    <View style={[styles.overlay, { pointerEvents: 'box-none' }]}>
      <View style={[styles.backdrop, { pointerEvents: 'none' }]} />

      <BlurView intensity={18} style={styles.blurContainer}>
        <View
          style={[styles.container, { maxHeight: cardMaxHeight }]}
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
        >
          <View style={styles.content}>
            <Text style={styles.title}>עוגיות ופרטיות</Text>

            <ScrollView
              style={[styles.descriptionScroll, { maxHeight: descriptionMaxHeight }]}
              contentContainerStyle={styles.descriptionScrollContent}
              showsVerticalScrollIndicator={false}
            >
              <Text style={styles.description}>
                אנחנו משתמשים בעוגיות כדי לשפר את החוויה שלך, לנתח שימוש באפליקציה
                ולספק תוכן מותאם אישית.
                {'\n'}
                באפשרותך לבחור האם לאפשר שימוש בעוגיות לא חיוניות. תוכל לשנות את
                בחירתך בכל עת דרך ההגדרות.
              </Text>
            </ScrollView>

            <TouchableOpacity
              onPress={handleOpenPolicy}
              activeOpacity={0.8}
              style={styles.policyLinkWrapper}
            >
              <Text style={styles.policyLinkText}>קרא עוד במדיניות הפרטיות שלנו</Text>
            </TouchableOpacity>

            <View style={styles.buttons}>
              <TouchableOpacity
                style={styles.rejectButton}
                onPress={handleReject}
                activeOpacity={0.85}
              >
                <Text style={styles.rejectButtonText}>אל תאפשר עוגיות מיותרות</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.acceptButton}
                onPress={handleAccept}
                activeOpacity={0.9}
              >
                <Text style={styles.acceptButtonText}>מאפשר עוגיות</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </BlurView>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    justifyContent: 'flex-end',
    paddingHorizontal: 14,
    paddingBottom: Platform.select({
      ios: 2,
      android: 4,
      web: 0,
      default: 4,
    }),
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(17, 24, 39, 0.22)',
  },
  blurContainer: {
    width: '100%',
    alignItems: 'center',
    transform: Platform.OS === 'web' ? [{ translateY: 8 }] : [{ translateY: 0 }],
  },

  // ✅ רקע לבן + נגיעה סגולה בהירה
  container: {
    width: '100%',
    maxWidth: 560,

    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(176, 38, 255, 0.22)',

    shadowColor: 'rgba(0, 0, 0, 1)',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.18,
    shadowRadius: 14,

    elevation: 10,
    overflow: 'hidden',
  },

  content: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: LIGHT_PURPLE_BG, // שכבה עדינה בתוך הכרטיס (אפשר למחוק אם אתה רוצה 100% לבן)
  },

  // ✅ טקסט כהה (לא לבן על לבן)
  title: {
    fontSize: 17,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 6,
    textAlign: 'right',
    fontFamily: Platform.select({
      ios: 'Heebo-Bold',
      android: 'Heebo-Bold',
      default: 'System',
    }),
  },

  descriptionScroll: {
    marginBottom: 8,
  },
  descriptionScrollContent: {
    paddingBottom: 2,
  },
  description: {
    fontSize: 13,
    color: '#374151',
    lineHeight: 19,
    textAlign: 'right',
    fontFamily: Platform.select({
      ios: 'Heebo-Regular',
      android: 'Heebo-Regular',
      default: 'System',
    }),
  },

  policyLinkWrapper: {
    alignItems: 'flex-end',
    marginBottom: 10,
  },
  policyLinkText: {
    fontSize: 12,
    textDecorationLine: 'underline',
    color: BRAND_PURPLE,
    fontFamily: Platform.select({
      ios: 'Heebo-Medium',
      android: 'Heebo-Medium',
      default: 'System',
    }),
  },

  buttons: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 10,
    marginTop: 2,
  },

  // ✅ כפתור דחייה בהיר
  rejectButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  rejectButtonText: {
    color: '#111827',
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
    fontWeight: '600',
    fontFamily: Platform.select({
      ios: 'Heebo-Medium',
      android: 'Heebo-Medium',
      default: 'System',
    }),
  },

  // ✅ כפתור אישור סגול מותג
  acceptButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 999,
    backgroundColor: BRAND_PURPLE,

    shadowColor: 'rgba(176, 38, 255, 1)',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.18,
    shadowRadius: 8,

    elevation: 4,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  acceptButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
    fontWeight: '700',
    fontFamily: Platform.select({
      ios: 'Heebo-Bold',
      android: 'Heebo-Bold',
      default: 'System',
    }),
  },
});
